import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface SyncContextType {
  lastSyncDate: string | null;
  syncSource: string;
  pipelineRunDate: string | null;
  loading: boolean;
  updateSyncDate: (dateString: string, source?: string) => void;
  refreshSyncDate: () => void;
  formatSyncDate: (dateString: string) => string;
  formatDBDate: (dateString: string) => string;
  formatFullSyncDate: (dateString: string) => string;
  formattedSyncDate: string;
  fullFormattedSyncDate: string;
  formattedPipelineRunDate: string;
  fullFormattedPipelineRunDate: string;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export function useSyncData(): SyncContextType {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncData must be used within a SyncProvider');
  }
  return context;
}

interface SyncProviderProps {
  children: ReactNode;
}

export function SyncProvider({ children }: SyncProviderProps) {
  const [lastSyncDate, setLastSyncDate] = useState<string | null>(null);
  const [syncSource, setSyncSource] = useState('');
  const [pipelineRunDate, setPipelineRunDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadSyncDate = async (forceRefresh = false) => {
    try {
      setLoading(true);

      // Add cache busting parameter to avoid browser cache
      const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';

      console.log('🔄 Loading summary metadata from clients-summary.json...', { forceRefresh, cacheBuster });

      // Load both dates from clients-summary.json (single source of truth)
      const summaryResponse = await fetch(`/clients-summary.json${cacheBuster}`);

      if (!summaryResponse.ok) {
        throw new Error(`Failed to fetch clients-summary.json: ${summaryResponse.status}`);
      }

      const summaryData = await summaryResponse.json();

      // Summary generation time (metadata.generatedAt) — used for "Last sync" in the UI
      const generatedAt = summaryData?.metadata?.generatedAt;
      if (generatedAt) {
        setPipelineRunDate(generatedAt);
        console.log('✅ Loaded generatedAt:', generatedAt);
      } else {
        throw new Error('generatedAt not found in clients-summary.json metadata');
      }

      // Optional: DB sync stamp from upstream client JSONs (propagated into summary)
      const lastDataSyncDate = summaryData?.metadata?.last_data_sync_date;
      if (lastDataSyncDate) {
        setLastSyncDate(lastDataSyncDate);
        setSyncSource('Database Sync');
        console.log('✅ Loaded last_data_sync_date:', lastDataSyncDate);
      } else {
        setLastSyncDate(null);
        setSyncSource('Summary');
      }

      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading sync date:', error);
      setLastSyncDate(null);
      setSyncSource('Error');
      setLoading(false);
    }
  };

  // Load sync date when component mounts
  useEffect(() => {
    loadSyncDate();
  }, []);

  // Format date for display (shows relative time)
  const formatSyncDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffHours < 1) {
        return 'Just now';
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        });
      }
    } catch (error) {
      console.error('Error formatting sync date:', error);
      return 'Unknown';
    }
  };

  // Format actual date for DB sync (shows actual date, not relative time)
  const formatDBDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting DB date:', error);
      return 'Unknown';
    }
  };

  // Format full date for tooltip
  const formatFullSyncDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';

    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      console.error('Error formatting full sync date:', error);
      return 'Unknown';
    }
  };

  const updateSyncDate = (dateString: string, source = '') => {
    setLastSyncDate(dateString);
    setSyncSource(source);
  };

  const refreshSyncDate = () => {
    console.log('Refreshing summary metadata (generatedAt)...');
    loadSyncDate(true);
  };

  const value: SyncContextType = {
    lastSyncDate,
    syncSource,
    pipelineRunDate,
    loading,
    updateSyncDate,
    refreshSyncDate,
    formatSyncDate,
    formatDBDate,
    formatFullSyncDate,
    formattedSyncDate: loading ? 'Loading...' : formatDBDate(pipelineRunDate || ''),
    fullFormattedSyncDate: loading ? 'Loading sync date...' : formatFullSyncDate(pipelineRunDate || ''),
    formattedPipelineRunDate: loading ? 'Loading...' : formatSyncDate(pipelineRunDate || ''),
    fullFormattedPipelineRunDate: loading ? 'Loading pipeline run date...' : formatFullSyncDate(pipelineRunDate || '')
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
}
